import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { FaPhoneAlt, FaTimes, FaCheck } from 'react-icons/fa';
import AppDropdown from '../../../components/AppDropdown';
import LeadCustomerInput from './LeadCustomerInput';
import { crmLeadApi } from '../../../api/crmApi';
import '../CRM.css';

const CALL_OUTCOMES = ['Connected', 'Not Connected', 'Busy', 'Call Back Later', 'Interested', 'Not Interested', 'Wrong Number', 'Converted'];
const INTEREST_LEVELS = ['High', 'Medium', 'Low', 'Not Interested'];
const OUTCOME_OPTIONS = CALL_OUTCOMES.map((o) => ({ value: o, label: o }));
const INTEREST_OPTIONS = INTEREST_LEVELS.map((o) => ({ value: o, label: o }));
const STATUS_OPTIONS = ['Contacted', 'Interested', 'Not Interested', 'Qualified', 'Requirement', 'Quotation', 'Negotiation'].map((s) => ({ value: s, label: s }));

export const CallModal = ({ open, onClose, lead, onSave, onSaved }) => {
  const buildForm = () => ({
    leadCustomer: lead ? `${lead.customerName || ''}${lead.company ? ' — ' + lead.company : ''}` : '',
    callDate: new Date().toISOString().slice(0, 10),
    callTime: new Date().toTimeString().slice(0, 5),
    duration: '',
    outcome: 'Connected',
    interestLevel: 'Medium',
    notes: '',
    nextFollowup: '',
    updateStatus: '',
    createFollowup: false,
    leadId: lead?.id ?? null,
  });
  const [form, setForm] = useState(buildForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(buildForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lead]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSelectTarget = (item) => {
    setForm((p) => ({
      ...p,
      leadCustomer: item.label,
      leadId: item.kind === 'Lead' ? item.id : null,
      customerId: item.kind === 'Customer' ? item.id : null,
    }));
  };

  const handleLeadCustomerText = (t) =>
    setForm((p) => ({
      ...p,
      leadCustomer: t,
      leadId: null,
      customerId: null,
    }));

  const handleSave = async () => {
    if (!form.notes.trim()) { toast.error('Please add call notes.'); return; }
    if (form.createFollowup && !form.nextFollowup) { toast.error('Pick a next follow-up date to create the follow-up.'); return; }
    const leadId = form.leadId ?? lead?.id ?? null;
    setSaving(true);
    try {
      await onSave(form);
      if (form.createFollowup) {
        if (leadId == null) {
          toast.info('Call saved. Select a lead to attach a follow-up.');
        } else {
          try {
            await crmLeadApi.addFollowup(leadId, {
              leadCustomer: form.leadCustomer,
              date: form.nextFollowup,
              time: form.callTime || '10:00',
              type: 'Call',
              priority: 'Medium',
              notes: form.notes,
              assignedTo: '',
              status: 'Pending',
              leadId,
            });
            toast.success('Follow-up created.');
          } catch {
            toast.error('Call saved, but the follow-up could not be created.');
          }
        }
      }
      onSaved?.();
    } catch {
      toast.error('Failed to save call.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="crm-modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="crm-modal" role="dialog" aria-modal="true" aria-label="Log call">
        <div className="crm-modal-header">
          <h3><FaPhoneAlt aria-hidden="true" /> Log Call</h3>
          <button type="button" className="crm-modal-close" onClick={onClose} aria-label="Close dialog"><FaTimes aria-hidden="true" /></button>
        </div>
        <div className="crm-modal-body">
          <div className="crm-form-grid">
            <LeadCustomerInput
              label="Lead / Customer"
              value={form.leadCustomer}
              onChange={handleLeadCustomerText}
              onSelect={handleSelectTarget}
            />
            <div className="crm-field">
              <label>Call Date <span className="req">*</span></label>
              <input type="date" value={form.callDate} onChange={set('callDate')} />
            </div>
            <div className="crm-field">
              <label>Call Time</label>
              <input type="time" value={form.callTime} onChange={set('callTime')} />
            </div>
            <div className="crm-field">
              <label>Duration (seconds)</label>
              <input type="number" value={form.duration} onChange={set('duration')} placeholder="e.g. 120" />
            </div>
            <div className="crm-field">
              <label>Call Outcome <span className="req">*</span></label>
              <AppDropdown value={form.outcome} options={OUTCOME_OPTIONS}
                onChange={(v) => setForm((p) => ({ ...p, outcome: v }))}
                menuWidth={220} menuClassName="crm-dropdown-window" />
            </div>
            <div className="crm-field">
              <label>Interest Level</label>
              <AppDropdown value={form.interestLevel} options={INTEREST_OPTIONS}
                onChange={(v) => setForm((p) => ({ ...p, interestLevel: v }))}
                menuWidth={200} menuClassName="crm-dropdown-window" />
            </div>
            <div className="crm-field">
              <label>Next Follow-up Date</label>
              <input type="date" value={form.nextFollowup} onChange={set('nextFollowup')} />
            </div>
            <div className="crm-field">
              <label>Update Lead Status</label>
              <AppDropdown value={form.updateStatus} options={STATUS_OPTIONS}
                onChange={(v) => setForm((p) => ({ ...p, updateStatus: v }))}
                placeholder="— No change —" menuWidth={200} menuClassName="crm-dropdown-window" />
            </div>
            <div className="crm-field crm-field-full">
              <label>Notes <span className="req">*</span></label>
              <textarea value={form.notes} onChange={set('notes')} placeholder="Call notes..." />
            </div>
            <div className="crm-field crm-field-full">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" className="crm-checkbox" checked={form.createFollowup}
                  onChange={(e) => setForm((p) => ({ ...p, createFollowup: e.target.checked }))} />
                Create follow-up from this call
              </label>
            </div>
          </div>
        </div>
        <div className="crm-modal-footer">
          <button type="button" className="crm-btn crm-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="crm-btn crm-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : <><FaCheck aria-hidden="true" /> Save Call</>}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CallModal;
